import { useCallback } from "react";
import { useBookingCodeStore } from "../stores/bookingCodeStore";
import { useBookingStore } from "../stores/bookingStore";
import { useResolveBookingCode } from "../api/api-hooks/booking-code";
import { useGetReservation, useUpdateReservation } from "../api/api-hooks/reservation";
import { useHeader } from "../contexts/HeaderContext";
import { queryClient } from "../query-client";
import type { BookingCodeRule } from "../api/contracts/booking-code";

/**
 * Reconciles a draft booking code with the store as part of the unified search
 * commit. Returns a function the search-commit handler awaits BEFORE flushing
 * dates/guests + firing onSearch — `useSearch` reads the store's applied code,
 * so the store must be settled first.
 *
 * Non-disclosing: the code is ALWAYS applied to the store as typed — there is
 * no validity check anywhere on the client, and the resolve call exists only
 * to fetch display rules for the (i) tooltip (bogus codes and valid codes
 * without rules both come back as `rules: []`). Whether a code "worked" only
 * ever shows up as adjusted prices. Removing a code is explicit — commit an
 * empty draft.
 *
 * When a cart already exists, the code is also PATCHed onto the reservation. The
 * store alone only steers the catalog search: without this, applying a code on
 * the booking page changed nothing about what the guest is charged, and REMOVING
 * one left the reservation discounted while the pill said it was gone — the cart
 * and the price disagreeing, with the money on the wrong side.
 */
export const useBookingCodeCommit = (siteId: string) => {
  // Per-field selectors: this hook is mounted by the search bar itself, whose
  // input writes `draft` to the same store on every keystroke.
  const storeCode = useBookingCodeStore((s) => s.code);
  const applied = useBookingCodeStore((s) => s.applied);
  const applyCode = useBookingCodeStore((s) => s.applyCode);
  const clearCode = useBookingCodeStore((s) => s.clearCode);
  const { mutateAsync: resolveCode } = useResolveBookingCode();
  const { mutateAsync: updateReservation } = useUpdateReservation();
  const reservationKey = useBookingStore((state) => state.reservationKey);
  const { selectedLanguage } = useHeader();

  return useCallback(
    async (draft: string) => {
      const trimmed = draft.trim();
      const currentApplied = applied ? (storeCode ?? "") : "";

      // No change since what's already applied — nothing to reconcile.
      if (trimmed === currentApplied) return;

      /**
       * Push the change onto an existing cart. `code: ""` clears the agreement
       * server-side, and the backend re-checks the agreement's rules and
       * reprices slots and products from there. The store is updated only after
       * this succeeds, so a failed PATCH leaves the pill matching what the
       * reservation is actually priced with rather than lying about it.
       */
      const syncReservation = async (code: string) => {
        if (!reservationKey) return;
        await updateReservation({
          reservationKey,
          siteId,
          data: { code },
          languageCode: selectedLanguage,
        });
        // Cart, checkout and confirmation all read the same reservation query,
        // so one invalidate re-prices every surface.
        await queryClient.invalidateQueries({ queryKey: useGetReservation.getKey() });
      };

      if (!trimmed) {
        await syncReservation("");
        clearCode();
        return;
      }

      let rules: BookingCodeRule[] = [];
      try {
        rules = (await resolveCode({ code: trimmed, siteId })).rules ?? [];
      } catch {
        // Rules are cosmetic — a failed fetch (429, network) must not block
        // applying the code.
      }
      await syncReservation(trimmed);
      applyCode({ code: trimmed, rules });
    },
    [
      siteId,
      applied,
      storeCode,
      applyCode,
      clearCode,
      resolveCode,
      reservationKey,
      updateReservation,
      selectedLanguage,
    ],
  );
};
